import { AltRoute, Close, Place, Search } from "@mui/icons-material";
import {
  ClickAwayListener,
  Fade,
  Paper,
  Popper,
  TextField,
} from "@mui/material";
import { Marker, useControl } from "@vis.gl/react-maplibre";
import clsx from "clsx";
import { FC } from "react";
import { createPortal } from "react-dom";

import {
  AccessControl,
  Address,
  MAPLIBRE_MARKER_VIEWPORT_ALIGNMENT,
} from "@ryde/controls";
import {
  useCourseWaypointActions,
  useRideWaypointActions,
  useStationRouteWaypointActions,
} from "@ryde/redux";
import {
  CourseMetadataKeys,
  onActivateKeyDown,
  STATION_ROUTE_WAYPOINT_POPPER_OFFSET_X,
} from "@ryde/shared";
import { ScreenDepth } from "@ryde/theme";

import { StationRouteWaypointMapClick } from "./station-route-waypoint-map-click";
import { stopMapEventPropagation } from "./station-route-waypoint-map-click.utils";
import { StationRouteWaypointSegmentPicker } from "./station-route-waypoint-segment-picker";
import {
  StationRouteWaypointAddressField,
  StationRouteWaypointCustomNameField,
  StationRouteWaypointHoverAnchor,
  StationRouteWaypointHoverMenu,
  StationRouteWaypointMapBtn,
  StationRouteWaypointPendingHost,
  StationRouteWaypointPendingShell,
  StationRouteWaypointPopperPanel,
  StationRouteWaypointToolbarContainer,
} from "./station-route-waypoint.styled";
import {
  IUseStationRouteWaypointMapControlsParams,
  useStationRouteWaypointMapControls,
} from "./use-station-route-waypoint-map-controls";
import { WaypointMapLibreControl } from "./waypoint-maplibre-control";

const WaypointMapControls: FC<IUseStationRouteWaypointMapControlsParams> = ({
  stations,
  routePolyline,
  addWaypointFromDraft,
}) => {
  const waypointControl = useControl(() => new WaypointMapLibreControl(), {
    position: "top-left",
  });
  const {
    mapContainer,
    anchorRef,
    toolbarRef,
    popperContentRef,
    t,
    addressOptions,
    addressValue,
    canAddWaypoint,
    canConfirm,
    closeAll,
    customNameDraft,
    handleAddressChange,
    handleClickAway,
    handleConfirm,
    handleCustomNameChange,
    handleLocationPicked,
    handleSelectInsertAfterIndex,
    handleTriggerClick,
    isButtonActive,
    isMapClickMode,
    isPopperOpen,
    isSearchPanelOpen,
    pendingDraft,
    setAddressOptions,
    startMapClickMode,
    startSearchMode,
  } = useStationRouteWaypointMapControls({
    stations,
    routePolyline,
    addWaypointFromDraft,
  });

  const toolbar = (
    <StationRouteWaypointToolbarContainer
      ref={toolbarRef}
      onMouseDown={stopMapEventPropagation}
      onClick={stopMapEventPropagation}
      onDoubleClick={stopMapEventPropagation}
      onWheel={stopMapEventPropagation}
    >
      <StationRouteWaypointHoverAnchor ref={anchorRef}>
        <StationRouteWaypointMapBtn
          $active={isButtonActive}
          $disabled={!canAddWaypoint}
          role="button"
          tabIndex={canAddWaypoint ? 0 : -1}
          aria-disabled={!canAddWaypoint}
          aria-label={t("stationRoute.waypoint.ADD_WAYPOINT")}
          aria-expanded={isPopperOpen}
          onClick={handleTriggerClick}
          onKeyDown={onActivateKeyDown(handleTriggerClick)}
        >
          <AltRoute />
        </StationRouteWaypointMapBtn>
      </StationRouteWaypointHoverAnchor>
    </StationRouteWaypointToolbarContainer>
  );

  return (
    <>
      {createPortal(toolbar, waypointControl.getContainer())}

      <StationRouteWaypointMapClick
        isActive={isMapClickMode}
        onLocationPicked={handleLocationPicked}
        toolbarRef={toolbarRef}
        popperContentRef={popperContentRef}
      />

      {pendingDraft && (
        <Marker
          longitude={pendingDraft.longitude}
          latitude={pendingDraft.latitude}
          anchor="center"
          {...MAPLIBRE_MARKER_VIEWPORT_ALIGNMENT}
        >
          <StationRouteWaypointPendingShell />
        </Marker>
      )}

      <Popper
        anchorEl={anchorRef.current}
        open={isPopperOpen}
        container={mapContainer}
        placement="right-start"
        style={{ zIndex: ScreenDepth.MAP_ATTRIBUTIONS }}
        popperOptions={{
          modifiers: [
            {
              name: "offset",
              options: { offset: [0, STATION_ROUTE_WAYPOINT_POPPER_OFFSET_X] },
            },
          ],
        }}
        transition
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={200}>
            <Paper
              elevation={4}
              ref={popperContentRef}
              onMouseDown={stopMapEventPropagation}
              onClick={stopMapEventPropagation}
              onDoubleClick={stopMapEventPropagation}
              onWheel={stopMapEventPropagation}
            >
              <ClickAwayListener onClickAway={handleClickAway}>
                {isSearchPanelOpen ? (
                  <StationRouteWaypointPopperPanel>
                    <div className="panel-header">
                      <div className="panel-title">
                        {t("stationRoute.waypoint.ADD_WAYPOINT")}
                      </div>
                      <button
                        type="button"
                        className="panel-close"
                        aria-label={t("general.CLOSE")}
                        onClick={closeAll}
                      >
                        <Close fontSize="small" />
                      </button>
                    </div>
                    <div className="panel-hint">
                      {t("stationRoute.waypoint.MAP_OR_SEARCH_HINT")}
                    </div>

                    <StationRouteWaypointAddressField fullWidth>
                      <Address
                        value={addressValue}
                        defaultOptions={addressOptions}
                        onChange={handleAddressChange}
                        onOptionsChange={setAddressOptions}
                        label={t("stationRoute.waypoint.ADDRESS_LABEL")}
                        className="addressAutoCompleteInput"
                      />
                    </StationRouteWaypointAddressField>

                    <StationRouteWaypointCustomNameField fullWidth>
                      <TextField
                        fullWidth
                        value={customNameDraft}
                        onChange={handleCustomNameChange}
                        label={t("stationRoute.waypoint.CUSTOM_NAME_LABEL")}
                      />
                    </StationRouteWaypointCustomNameField>

                    {pendingDraft && stations.length > 1 && (
                      <StationRouteWaypointSegmentPicker
                        stations={stations}
                        selectedInsertAfterIndex={pendingDraft.insertAfterIndex}
                        onSelectInsertAfterIndex={handleSelectInsertAfterIndex}
                      />
                    )}

                    <div className="panel-actions">
                      <div
                        className="panel-secondary-btn"
                        onClick={closeAll}
                        onKeyDown={onActivateKeyDown(closeAll)}
                        role="button"
                        tabIndex={0}
                      >
                        {t("general.CANCEL")}
                      </div>
                      <div
                        className={clsx("panel-primary-btn", {
                          disabled: !canConfirm,
                        })}
                        onClick={handleConfirm}
                        onKeyDown={onActivateKeyDown(handleConfirm)}
                        role="button"
                        tabIndex={0}
                      >
                        {t("stationRoute.waypoint.CONFIRM_ADD")}
                      </div>
                    </div>
                  </StationRouteWaypointPopperPanel>
                ) : (
                  <StationRouteWaypointHoverMenu>
                    <div className="menu-header">
                      <span className="menu-title">
                        {t("stationRoute.waypoint.ADD_WAYPOINT")}
                      </span>
                      <button
                        type="button"
                        className="menu-close"
                        aria-label={t("general.CLOSE")}
                        onClick={closeAll}
                      >
                        <Close fontSize="small" />
                      </button>
                    </div>
                    <div
                      className="menu-option"
                      onClick={startSearchMode}
                      onKeyDown={onActivateKeyDown(startSearchMode)}
                      role="button"
                      tabIndex={0}
                    >
                      <Search fontSize="small" />
                      {t("stationRoute.waypoint.MODE_SEARCH")}
                    </div>
                    <div
                      className="menu-option"
                      onClick={startMapClickMode}
                      onKeyDown={onActivateKeyDown(startMapClickMode)}
                      role="button"
                      tabIndex={0}
                    >
                      <Place fontSize="small" />
                      {t("stationRoute.waypoint.MODE_MAP_CLICK")}
                    </div>
                  </StationRouteWaypointHoverMenu>
                )}
              </ClickAwayListener>
            </Paper>
          </Fade>
        )}
      </Popper>
    </>
  );
};

export const StationRouteWaypointMapControlsHost: FC = () => {
  const { addWaypointFromDraft, stations, stationRouteDto } =
    useStationRouteWaypointActions();

  return (
    <AccessControl entity="stationRoute" action="update">
      <StationRouteWaypointPendingHost>
        <WaypointMapControls
          stations={stations}
          routePolyline={stationRouteDto?.result?.routePolyline}
          addWaypointFromDraft={addWaypointFromDraft}
        />
      </StationRouteWaypointPendingHost>
    </AccessControl>
  );
};

export const RideWaypointMapControlsHost: FC = () => {
  const { addWaypointFromDraft, stations, rideDto } = useRideWaypointActions();

  return (
    <AccessControl entity="ride" action="update">
      <StationRouteWaypointPendingHost>
        <WaypointMapControls
          stations={stations}
          routePolyline={rideDto?.metadata?.routePolyline}
          addWaypointFromDraft={addWaypointFromDraft}
        />
      </StationRouteWaypointPendingHost>
    </AccessControl>
  );
};

export const CourseWaypointMapControlsHost: FC = () => {
  const { addWaypointFromDraft, stations, courseDto } =
    useCourseWaypointActions();

  return (
    <AccessControl entity="course" action="update">
      <StationRouteWaypointPendingHost>
        <WaypointMapControls
          stations={stations}
          routePolyline={
            courseDto?.metadata?.[CourseMetadataKeys.routePolyline]
          }
          addWaypointFromDraft={addWaypointFromDraft}
        />
      </StationRouteWaypointPendingHost>
    </AccessControl>
  );
};
